import { t } from '../../utils/i18n';
import { appUrl, appointmentChannelId, form, locationId, locations, originProcedures, procedureIdSelected, procedures, proceduresOfAppointmentChannel, providerId, selectedProcedures, selectedStaffAppointmentChannel, sourceParam, staffId } from "../../utils/variables";
import { params } from "../events/movePages";
import { HOST_TYPE_ONE_ON_ONE, HOST_TYPE_ROUND_ROBIN, HOST_TYPE_COLLECTIVE } from "../../utils/constants";
import parsePhoneNumberFromString from "libphonenumber-js";
import { formatPhoneNumber } from "./countryCodeActions";

const DAY_ORDER = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];
const DAY_KEYS = {
    monday: 'day_mon',
    tuesday: 'day_tue',
    wednesday: 'day_wed',
    thursday: 'day_thu',
    friday: 'day_fri',
    saturday: 'day_sat',
    sunday: 'day_sun',
};

function esc(value) {
    if (value === null || value === undefined) return '';
    return String(value)
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;');
}

function formatMinutes(mins) {
    const total = Math.max(0, Math.round(Number(mins) || 0));
    if (!total) return '';
    const hours = Math.floor(total / 60);
    const minutes = total % 60;
    if (hours && minutes) return `${hours}h ${minutes}min`;
    if (hours) return `${hours}h`;
    return `${minutes} min`;
}

function formatClock(value) {
    // Hours come from config as "HH:mm" strings.
    const parsed = moment(value, 'HH:mm', true);
    return parsed.isValid() ? parsed.format('h:mm A') : String(value ?? '');
}

function timezoneAbbreviation(timezone) {
    if (!timezone) return '';
    try {
        const parts = new Intl.DateTimeFormat('en-US', { timeZone: timezone, timeZoneName: 'short' })
            .formatToParts(new Date());
        const abbr = parts.find(p => p.type === 'timeZoneName')?.value || '';
        const localParts = new Intl.DateTimeFormat('en-US', { timeZoneName: 'short' }).formatToParts(new Date());
        const localAbbr = localParts.find(p => p.type === 'timeZoneName')?.value || '';
        // Only worth showing when the practice is in a different zone than the visitor.
        return abbr && abbr !== localAbbr ? abbr : '';
    } catch (error) {
        return '';
    }
}

/**
 * Collapse an hours map into human lines, merging consecutive days that share
 * the same ranges: { monday..friday 09:00-17:00 } -> "Mon – Fri  9:00 AM – 5:00 PM".
 */
function buildHoursLines(hours) {
    if (!hours || typeof hours !== 'object') return [];
    const groups = [];
    for (const day of DAY_ORDER) {
        const ranges = (Array.isArray(hours[day]) ? hours[day] : []).filter(r => r?.from && r?.to);
        const key = ranges.map(r => `${r.from}-${r.to}`).join(',');
        const last = groups[groups.length - 1];
        if (last && last.key === key) {
            last.days.push(day);
        } else {
            groups.push({ key, days: [day], ranges });
        }
    }
    return groups
        .filter(group => group.ranges.length)
        .map(group => {
            const first = t(DAY_KEYS[group.days[0]]);
            const last = t(DAY_KEYS[group.days[group.days.length - 1]]);
            return {
                days: group.days.length > 1 ? `${first} – ${last}` : first,
                times: group.ranges.map(r => `${formatClock(r.from)} – ${formatClock(r.to)}`).join(', '),
            };
        });
}

function formatLocationPhone(phoneNumber, country) {
    if (!phoneNumber) return '';
    try {
        const parsed = parsePhoneNumberFromString(phoneNumber, country || 'US');
        if (parsed && parsed.isValid()) return parsed.formatNational();
        return formatPhoneNumber(phoneNumber, country || 'US');
    } catch (error) {
        return phoneNumber;
    }
}

function telHref(phoneNumber, country) {
    if (!phoneNumber) return '';
    try {
        const parsed = parsePhoneNumberFromString(phoneNumber, country || 'US');
        if (parsed && parsed.isValid()) return parsed.getURI();
    } catch (error) { /* fall through to the raw digits */ }
    const digits = String(phoneNumber).replace(/[^\d+]/g, '');
    return digits ? `tel:${digits}` : '';
}

function directionsUrl(location) {
    if (!location) return '';
    if (location.google_profile_url) return location.google_profile_url;
    const destination = (location.lat && location.long)
        ? `${location.lat},${location.long}`
        : [location.address1, location.address2, location.city, location.state, location.postal_code]
            .filter(Boolean).join(', ');
    if (!destination) return '';
    return `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(destination)}`;
}

function addressLines(location) {
    if (!location) return [];
    const street = [location.address1, location.address2].filter(Boolean).join(', ');
    const cityLine = [
        [location.city, location.state].filter(Boolean).join(', '),
        location.postal_code,
    ].filter(Boolean).join(' ');
    const lines = [street, cityLine].filter(Boolean);
    if (lines.length) return lines;
    return location.full_address ? [location.full_address] : [];
}

function staffFullName(staff) {
    return [staff?.first_name, staff?.last_name].filter(Boolean).join(' ').trim();
}

function canUserChooseStaff(hostType, staffCount) {
    switch (hostType) {
        case HOST_TYPE_ONE_ON_ONE:
            return staffCount > 1;
        case HOST_TYPE_ROUND_ROBIN:
        case HOST_TYPE_COLLECTIVE:
            return false;
        default:
            return form?.appointment_channel?.visitor_can_choose_the_staff;
    }
}

function getHostType() {
    return form?.appointment_channel?.host || HOST_TYPE_ONE_ON_ONE;
}

const DIVIDER = `<div class="divider"><hr /></div>`;

function row(icon, title, valueHtml, { extra = false, className = '' } = {}) {
    if (!valueHtml) return '';
    return `
        <div class="staffInfo__description ${extra ? 'staffInfo__extra' : ''} ${className}">
            <span class="staffInfo__description-icon"><i data-feather="${icon}"></i></span>
            <div class="staffInfo__description-left">
                <span class="staffInfo__description-title">${title}</span>
                ${valueHtml}
            </div>
        </div>
    `;
}

export function renderStaffInfoHeader(staff, procedure, container, isAppend = false) {
    const staffsOfAppointmentChannel = form?.appointment_channel?.staffs?.filter(staff => staff?.online_booking)
    const foundStaff = staff ?? form?.staff?.find(staff => staff.id === +staffId);
    /**
     * The service this panel describes. Callers resolve it out of the
     * project-level `procedures` list only (providersListeners/selectTime
     * listeners both do `procedures.find(...)`), and that list does NOT hold a
     * channel's own services — so on a scheduling link the visitor's pick
     * arrived here as undefined and the panel kept reading "18 services
     * available" with no duration all the way to the date/time screens.
     * Resolve it from the same selection state the services screen writes,
     * looked up across both procedure sources. Read-only: no flow state is
     * touched, and with several services still selected nothing is claimed.
     */
    const procedurePool = [
        ...(proceduresOfAppointmentChannel || []),
        ...(procedures || []),
        ...(originProcedures || []),
    ].filter(Boolean)
    const pickedProcedureId = +procedureIdSelected
        || (selectedProcedures?.length === 1 ? +selectedProcedures[0] : 0)
    const foundProcedure = procedure
        ?? (pickedProcedureId ? procedurePool.find(p => p?.id === pickedProcedureId) : null)
        ?? null
    const infoStaffContainer = document.querySelector(`${container ? '.' + container : ''} .infoStaff`);
    const isMultipleStaff = staffsOfAppointmentChannel?.length > 1;
    const { duration, location } = form.appointment_channel
    const selectedLocation = locationId ? locations?.find(l => l?.id === +locationId) : null
    const currentLocation = location ? location : (selectedLocation ? selectedLocation : locations?.length === 1 ? locations[0] : null)
    const descriptionAppointmentChannel = form?.appointment_channel?.description ?? ''
    const appointment_channel_name = form?.appointment_channel?.appointment_channel_name ?? ''
    const businessName = form?.project?.business_name ?? ''
    const businessPhoto = form?.project?.business_photo ?? ''

    const hostType = getHostType();
    const staffCount = staffsOfAppointmentChannel?.length || 0;
    const canChooseStaff = canUserChooseStaff(hostType, staffCount);

    let providerSelected = null
    if (appointmentChannelId && canChooseStaff) {
        providerSelected = staffsOfAppointmentChannel?.find(staff => staff?.id == selectedStaffAppointmentChannel)
    }

    // The one provider this panel represents: the explicitly selected one, the
    // staff carried in from the flow, or the channel's only bookable provider.
    const singleStaff = providerSelected
        ?? (appointmentChannelId
            ? (staffCount === 1 ? staffsOfAppointmentChannel[0] : null)
            : foundStaff)

    const MAX_STAFF_DISPLAY = 4

    // Providers this panel represents: the one the visitor picked, otherwise all
    // bookable ones on the channel.
    const listedStaffs = (appointmentChannelId
        ? ((canChooseStaff && providerSelected) ? [providerSelected] : staffsOfAppointmentChannel)
        : [foundStaff])?.filter(Boolean) || []

    /**
     * Initials, so a provider without a photo doesn't borrow the practice logo.
     * Four copies of a wide wordmark cropped into overlapping circles reads as
     * a rendering bug, which is exactly what multi-provider channels showed.
     */
    const initialsOf = (i) => {
        const parts = staffFullName(i).split(/\s+/).filter(Boolean)
        if (!parts.length) return ''
        return (parts[0][0] + (parts.length > 1 ? parts[parts.length - 1][0] : '')).toUpperCase()
    }

    const renderListStaff = () => {
        if (!listedStaffs.length) {
            return ``
        }

        const displayStaffs = listedStaffs.slice(0, MAX_STAFF_DISPLAY)
        const remainingCount = listedStaffs.length - MAX_STAFF_DISPLAY

        let iconStaffs = ``
        for (const i of displayStaffs) {
            const initials = initialsOf(i)
            const face = i?.photo
                ? `<img src="${encodeURI(i.photo)}" alt="">`
                : (initials
                    ? `<span class="staff-item__initials">${esc(initials)}</span>`
                    : `<img src="${appUrl}/assets/images/png/default-avatar.png" alt="">`)
            iconStaffs = iconStaffs.concat(`
                <div class="staff-item">
                    <div class="img">
                    ${face}
                    </div>
                    <span class="name">${esc(staffFullName(i))}</span>
                </div>
            `)
        }

        if (remainingCount > 0) {
            iconStaffs = iconStaffs.concat(`
                <div class="staff-item staff-item-more">
                    <div class="img">
                        <span class="staff-more-count">+${remainingCount}</span>
                    </div>
                </div>
            `)
        }

        return iconStaffs
    }

    /**
     * Identity image: the photo of the provider this appointment is actually
     * with, otherwise the practice logo. With several bookable providers and
     * none chosen yet there is no such provider, so we must NOT borrow the
     * first one's face — that presents an arbitrary provider as the one the
     * visitor is booking. The generic silhouette is the last resort only.
     */
    const renderIdentityImage = () => {
        const photo = singleStaff?.photo || (appointmentChannelId ? '' : foundStaff?.photo)
        if (photo) {
            return `<div class="img"><img src="${encodeURI(photo)}" alt="avatar"></div>`
        }
        if (businessPhoto) {
            return `<div class="img img--logo"><img src="${encodeURI(businessPhoto)}" alt="${esc(businessName)}"></div>`
        }
        return `<div class="img"><img src="${appUrl}/assets/images/png/default-avatar.png" alt="avatar"></div>`
    }

    const renderStaffInfo = () => {
        const heading = appointment_channel_name || businessName
        // Channel names often already carry the practice name ("Acme — Book an Appointment"),
        // so only add the practice line when it isn't already in the heading.
        const subtitle = businessName && !heading.toLowerCase().includes(businessName.toLowerCase()) ? businessName : ''

        return `
            ${renderIdentityImage()}
            <div class="staffInfo__identity">
                <h3>${esc(heading)}</h3>
                ${subtitle ? `<span class="staffInfo__practice">${esc(subtitle)}</span>` : ''}
            </div>
        `
    }

    const channelProcedures = (appointmentChannelId ? proceduresOfAppointmentChannel : [foundProcedure]) || []
    const procedureList = channelProcedures.filter(Boolean)
    const selectedProcedure = foundProcedure ?? (procedureList.length === 1 ? procedureList[0] : null)

    const renderProviderRow = () => {
        // One provider (or one chosen out of many): just name them.
        if (!isMultipleStaff || providerSelected) {
            const name = staffFullName(singleStaff)
            if (!name) return ''
            return row('user', t('provider'), `<span class="staffInfo__description-name">${esc(name)}</span>`)
        }

        // Several bookable providers: avatars plus their names in writing, since
        // the avatar tooltip only exists on hover (and never on touch).
        const names = listedStaffs.map(staffFullName).filter(Boolean)
        if (!names.length) return ''
        const shown = names.slice(0, MAX_STAFF_DISPLAY).map(esc).join(', ')
        const remaining = names.length - MAX_STAFF_DISPLAY
        const value = `
            <div class="list-staff">${renderListStaff()}</div>
            <span class="staffInfo__providers-names">${shown}${remaining > 0 ? ` +${remaining} ${t('more')}` : ''}</span>
        `
        return row('users', t('providers'), value, { className: 'staffInfo__providers' })
    }

    /**
     * Duration is only shown once it is actually DECIDED: a channel-level fixed
     * duration, or the one service on the channel, or the service the visitor
     * picked. When several services are still on the table, any number here —
     * including a "30–60 min" range — is a guess the booking won't honour, so
     * the row is omitted and appears later in the flow once a service is chosen.
     */
    const renderDurationRow = () => {
        const minutes = duration > 0 ? duration : (selectedProcedure?.duration || 0)
        if (!(minutes > 0)) return ''
        const text = formatMinutes(minutes)
        if (!text) return ''
        return row('clock', t('duration_label'), `<span class="staffInfo__description-name">${esc(text)}</span>`)
    }

    const renderTypeRow = () => {
        if (!procedureList.length) {
            return `<div class="staffInfo__description"><div class="staffInfo__description-left">${t('no_services')}</div></div>`
        }
        const text = selectedProcedure?.name
            ? selectedProcedure.name
            : `${procedureList.length} ${t('services_available')}`
        return row('activity', t('appointment_type'), `<span class="staffInfo__description-name">${esc(text)}</span>`)
    }

    const renderLocationRow = () => {
        if (!currentLocation) return ''

        const hasVirtualVisit = form?.appointment_channel?.is_virtual_meeting;
        if (hasVirtualVisit) {
            const showPhone = form?.appointment_channel?.show_phone_in_virtual_location !== false;
            if (!showPhone) {
                return row('video', t('location'), `<span class="staffInfo__description-name">${t('virtual_meeting')}</span>`)
            }
            const formattedPhone = formatLocationPhone(currentLocation?.phone, currentLocation?.country)
            return row('video', t('location'), `<span class="staffInfo__description-name">${t('virtual')} ${esc(formattedPhone)}</span>`)
        }

        const name = currentLocation?.name || ''
        const lines = addressLines(currentLocation)
        const directions = directionsUrl(currentLocation)
        const primary = name || lines.shift() || ''
        if (!primary) return ''

        return row('map-pin', t('location'), `
            <span class="staffInfo__description-name">${esc(primary)}</span>
            ${lines.length ? `<span class="staffInfo__address staffInfo__extra">${lines.map(esc).join('<br />')}</span>` : ''}
            ${directions ? `<a class="staffInfo__link staffInfo__extra" href="${esc(directions)}" target="_blank" rel="noopener noreferrer">${t('get_directions')}</a>` : ''}
        `)
    }

    const renderPhoneRow = () => {
        if (form?.appointment_channel?.is_virtual_meeting) return ''
        const phone = currentLocation?.phone || form?.project?.business_phone || ''
        if (!phone) return ''
        const country = currentLocation?.country || 'US'
        const formatted = formatLocationPhone(phone, country) || phone
        const href = telHref(phone, country)
        const value = href
            ? `<a class="staffInfo__description-name staffInfo__link" href="${esc(href)}">${esc(formatted)}</a>`
            : `<span class="staffInfo__description-name">${esc(formatted)}</span>`
        return row('phone', t('phone_label'), value, { extra: true })
    }

    const renderHoursRow = () => {
        const availabilities = form?.appointment_channel?.location_availabilities || []
        const matched = currentLocation
            ? availabilities.find(a => a?.location_id === currentLocation.id)
            : null
        const hours = matched?.availability?.hours
            || form?.appointment_channel?.availability_hours
            || currentLocation?.hours
        const timezone = matched?.availability?.timezone
            || form?.appointment_channel?.availability_timezone
            || currentLocation?.timezone

        const lines = buildHoursLines(hours)
        if (!lines.length) return ''

        const abbr = timezoneAbbreviation(timezone)
        const title = abbr ? `${t('office_hours')} (${esc(abbr)})` : t('office_hours')

        return row('calendar', title, `
            <span class="staffInfo__hours">
                ${lines.map(line => `
                    <span class="staffInfo__hours-line">
                        <span class="staffInfo__hours-days">${esc(line.days)}</span>
                        <span class="staffInfo__hours-times">${esc(line.times)}</span>
                    </span>
                `).join('')}
            </span>
        `, { extra: true })
    }

    const renderDescriptionInfo = () => {
        if (!descriptionAppointmentChannel) return ''
        return row('file-text', t('description'), `<span class="staffInfo__description-name">${esc(descriptionAppointmentChannel)}</span>`, { extra: true })
    }

    if (infoStaffContainer) {
        const rows = [
            renderProviderRow(),
            renderDurationRow(),
            renderTypeRow(),
            renderLocationRow(),
            renderPhoneRow(),
            renderHoursRow(),
            renderDescriptionInfo(),
        ].filter(Boolean)

        const infoStaffHeaderHTML = `
            <div class="staffInfo__wrapper">
                ${renderStaffInfo()}
                ${rows.length ? `<div class="staffInfo__rows">${rows.join(DIVIDER)}</div>` : ''}
            </div>
        `
        if (isAppend) {
            infoStaffContainer.innerHTML = ''
            infoStaffContainer.insertAdjacentHTML('afterbegin', `${infoStaffHeaderHTML}`);
        } else {
            infoStaffContainer.innerHTML = infoStaffHeaderHTML
        }
    }
    feather.replace();
}
